import React, { useState, useMemo, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setModuleFuel } from "../../../store/features/moduleSlice";
import { formatNumber, formatCurrency } from "../../../utils/formatting";
import { FUEL_DURATIONS } from "../../../constants/fuel";
import { calculateJumpFuelConsumption, calculatePowerPlantFuelConsumption } from "../../../utils/calculations/fuelCalculations";
import TonnageInput from "../../common/TonnageInput";

/**
 * Module Fuel Step Component
 * Handles fuel tank configuration for modules
 */
export const ModuleFuelStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const fuel = useAppSelector((state) => state.module.moduleConfig?.systems.fuel);
  const drives = useAppSelector((state) => state.module.moduleConfig?.systems.drives || []);
  const powerPlants = useAppSelector((state) => state.module.moduleConfig?.systems.powerPlant || []);

  // State for fuel configuration
  const [fuelMode, setFuelMode] = useState<string>(fuel?.mode || "none");
  const [customFuelTons, setCustomFuelTons] = useState<number>(fuel?.customTons || 0);
  const [fuelDuration, setFuelDuration] = useState<number>(fuel?.duration || 4); // 4 weeks default

  // Get installed drives (manoeuvre drives don't require fuel according to Traveller rules)
  const jumpDrive = drives.find(d => d.type === "JUMP");
  const reactionDrive = drives.find(d => d.type === "REACTION");

  // Calculate fuel requirements based on installed drives
  const calculateFuelRequirements = useMemo(() => {
    if (!moduleConfig) {
      return {
        powerPlant: 0,
        jump: 0,
        reaction: 0,
        total: 0
      };
    }

    let powerPlantFuel = 0;
    let jumpFuel = 0;
    let reactionFuel = 0;

    // Power plant fuel - duration affects consumption
    // Calculate fuel for all installed power plants in the module
    if (powerPlants.length > 0) {
      powerPlants.forEach((plant: any) => {
        // Convert module power plant to format expected by calculation function
        const powerPlant = {
          type: (plant.fuelType || 'fusion') as any, // Default to fusion if not specified
          rating: 1, // Module power plants don't use rating for fuel calculations
          cost: plant.cost || 0,
          weight: plant.tonnage || 0,
          power: plant.output || 0,
          techLevel: plant.techLevel || 8,
          maintenanceCost: 0,
          description: plant.description || ''
        };
        powerPlantFuel += calculatePowerPlantFuelConsumption(powerPlant, fuelDuration);
      });
    } else {
      // If no power plant, estimate minimal fuel requirement
      powerPlantFuel = Math.max(1, fuelDuration * 0.25); // Conservative estimate
    }

    // Jump drive fuel - duration doesn't affect this (per jump)
    if (jumpDrive) {
      jumpFuel = calculateJumpFuelConsumption(jumpDrive.rating, moduleConfig.size);
    }

    // Reaction drive fuel - duration affects consumption
    if (reactionDrive) {
      const hoursPerWeek = 168; // 7 days × 24 hours
      const totalHours = fuelDuration * hoursPerWeek;
      
      if (reactionDrive.rating === 0) {
        reactionFuel = 0.25 * totalHours; // 0.25 tons per hour for thrust 0
      } else {
        reactionFuel = moduleConfig.size * 0.025 * reactionDrive.rating * totalHours; // 2.5% per thrust per hour
      }
    }

    return {
      powerPlant: powerPlantFuel,
      jump: jumpFuel,
      reaction: reactionFuel,
      total: powerPlantFuel + jumpFuel + reactionFuel
    };
  }, [moduleConfig, jumpDrive, reactionDrive, powerPlants, fuelDuration]);

  // Calculate actual fuel tons based on mode
  const actualFuelTons = useMemo(() => {
    switch (fuelMode) {
      case "none":
        return 0;
      case "auto":
        return calculateFuelRequirements.total;
      case "custom":
        return customFuelTons;
      default:
        return 0;
    }
  }, [fuelMode, calculateFuelRequirements.total, customFuelTons]);

  // Calculate fuel cost (1000 Cr per ton)
  const fuelCost = useMemo(() => {
    return actualFuelTons * 1000;
  }, [actualFuelTons]);

  // Update fuel configuration when mode or values change
  useEffect(() => {
    if (fuelMode === "none") {
      dispatch(setModuleFuel(null));
    } else {
              const fuelConfig = {
          mode: fuelMode,
          tons: actualFuelTons,
          customTons: customFuelTons,
          duration: fuelDuration,
          cost: fuelCost,
          techLevel: 7,
          description: fuelMode === "none" ? "No fuel tanks installed" : 
                      fuelMode === "auto" ? `Auto-calculated fuel based on installed drives (${formatNumber(calculateFuelRequirements.total)} tons)` :
                      `Custom fuel capacity (${formatNumber(customFuelTons)} tons)`,
          power: 0,
          maintenanceCost: 0
        };
      dispatch(setModuleFuel(fuelConfig));
    }
  }, [fuelMode, actualFuelTons, customFuelTons, fuelDuration, fuelCost, dispatch, calculateFuelRequirements.total]);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Handle fuel mode change
  const handleFuelModeChange = (mode: string) => {
    setFuelMode(mode);
  };

  // Handle custom fuel tons change
  const handleCustomFuelTonsChange = (tons: number) => {
    setCustomFuelTons(Math.max(0, Math.min(tons, moduleConfig.size)));
  };

  // Handle duration change
  const handleDurationChange = (duration: number) => {
    setFuelDuration(duration);
  };



  // Calculate remaining tons
  const remainingTons = moduleConfig.size - actualFuelTons;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Module Fuel Tank Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Configure fuel storage for your module
        </p>
      </div>

      {/* Module Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Module Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.size)} tons</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Tech Level:</span>
            <div className="text-blue-900 dark:text-blue-100">TL {moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Installed Drives:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {drives.length > 0 ? drives.map(d => `${d.name}`).join(", ") : "None"}
            </div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Remaining Tons:</span>
            <div className={`font-semibold ${remainingTons < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatNumber(remainingTons)} tons
            </div>
          </div>
        </div>
      </div>

      {/* Fuel Requirements */}
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
          📊 Fuel Requirements ({FUEL_DURATIONS.find(d => d.value === fuelDuration)?.label})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-yellow-700 dark:text-yellow-300 font-medium">Power Plant:</span>
            <div className="text-yellow-900 dark:text-yellow-100">{formatNumber(calculateFuelRequirements.powerPlant)} tons</div>
          </div>
          <div>
            <span className="text-yellow-700 dark:text-yellow-300 font-medium">Jump Drive:</span>
            <div className="text-yellow-900 dark:text-yellow-100">{formatNumber(calculateFuelRequirements.jump)} tons</div>
          </div>
          <div>
            <span className="text-yellow-700 dark:text-yellow-300 font-medium">Reaction Drive:</span>
            <div className="text-yellow-900 dark:text-yellow-100">{formatNumber(calculateFuelRequirements.reaction)} tons</div>
          </div>
          <div>
            <span className="text-yellow-700 dark:text-yellow-300 font-medium">Total Required:</span>
            <div className="text-yellow-900 dark:text-yellow-100 font-semibold">{formatNumber(calculateFuelRequirements.total)} tons</div>
          </div>
        </div>

      </div>

      {/* Fuel Configuration */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Fuel Configuration
        </h3>
        
        <div className="space-y-4">
          {/* Fuel Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fuel Configuration
            </label>
            <select
              value={fuelMode}
              onChange={(e) => handleFuelModeChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="none">None</option>
              <option value="auto">Auto</option>
              <option value="custom">Custom Size</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {fuelMode === "none" && "No fuel tanks installed"}
              {fuelMode === "auto" && "Automatically calculate fuel based on installed drives"}
              {fuelMode === "custom" && "Specify custom fuel capacity"}
            </p>
          </div>

          {/* Custom Fuel Amount */}
          {fuelMode === "custom" && (
            <div>
              <TonnageInput
                value={customFuelTons}
                onChange={handleCustomFuelTonsChange}
                systemType="fuelCapacity"
                category="fuelSystems"
                max={moduleConfig.size}
                label="Custom Fuel Capacity (tons)"
                placeholder="Enter fuel capacity in tons"
                className=""
                showValidation={true}
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum: {formatNumber(moduleConfig.size)} tons (module size)
              </p>
            </div>
          )}

          {/* Fuel Duration */}
          {fuelMode !== "none" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuel Duration
              </label>
              <select
                value={fuelDuration}
                onChange={(e) => handleDurationChange(parseInt(e.target.value))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {FUEL_DURATIONS.map((duration) => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Selected Fuel Details */}
        {fuelMode !== "none" && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-800 mb-3">Selected Fuel Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <p><span className="font-medium">Mode:</span> {fuelMode === "auto" ? "Auto-calculated" : "Custom"}</p>
                <p><span className="font-medium">Capacity:</span> {formatNumber(actualFuelTons)} tons</p>
                <p><span className="font-medium">Duration:</span> {FUEL_DURATIONS.find(d => d.value === fuelDuration)?.label}</p>
              </div>
              <div className="space-y-2">
                <p><span className="font-medium">Cost:</span> {formatCurrency(fuelCost)}</p>
                <p><span className="font-medium">Tech Level:</span> TL 7</p>
                <p><span className="font-medium">Power:</span> 0 Power</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer with Fuel Stats */}
      {fuelMode !== "none" && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
            <div>
              <span className="text-gray-400 block text-sm">Fuel Mode</span>
              <span className="text-xl font-bold text-yellow-400">
                {fuelMode === "auto" ? "Auto" : "Custom"}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Capacity</span>
              <span className="text-xl font-bold text-blue-400">
                {formatNumber(actualFuelTons)} tons
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Cost</span>
              <span className="text-xl font-bold text-green-400">
                {formatCurrency(fuelCost)}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Duration</span>
              <span className="text-xl font-bold text-purple-400">
                {FUEL_DURATIONS.find(d => d.value === fuelDuration)?.label}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
